/**
 * Jurisdiction display config for IP components.
 * Uses Font Awesome icons with custom colors and gradients — no emoji flags.
 */

export interface JurisdictionInfo {
  code: string;
  name: string;
  icon: string;         // Font Awesome class
  iconColor: string;
  gradient: string;     // CSS gradient for badge backgrounds
  shortName?: string;   // Abbreviated label
}

export const JURISDICTIONS: Record<string, JurisdictionInfo> = {
  US: {
    code: "US",
    name: "United States",
    icon: "fas fa-flag-usa",
    iconColor: "#1e40af",
    gradient: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
  },
  CA: {
    code: "CA",
    name: "Canada",
    icon: "fab fa-canadian-maple-leaf",
    iconColor: "#d52b1e",
    gradient: "linear-gradient(135deg, #fee2e2, #fecaca)",
  },
  EP: {
    code: "EP",
    name: "European",
    icon: "fas fa-building-columns",
    iconColor: "#1d4ed8",
    gradient: "linear-gradient(135deg, #dbeafe, #c7d2fe)",
    shortName: "EP",
  },
  PCT: {
    code: "PCT",
    name: "PCT (International)",
    icon: "fas fa-globe",
    iconColor: "#0F5132",
    gradient: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
    shortName: "PCT",
  },
  GB: {
    code: "GB",
    name: "United Kingdom",
    icon: "fas fa-crown",
    iconColor: "#7c3aed",
    gradient: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
  },
  AU: {
    code: "AU",
    name: "Australia",
    icon: "fas fa-earth-oceania",
    iconColor: "#d97706",
    gradient: "linear-gradient(135deg, #fef3c7, #fde68a)",
  },
  JP: {
    code: "JP",
    name: "Japan",
    icon: "fas fa-torii-gate",
    iconColor: "#dc2626",
    gradient: "linear-gradient(135deg, #fee2e2, #fecaca)",
  },
  KR: {
    code: "KR",
    name: "South Korea",
    icon: "fas fa-yin-yang",
    iconColor: "#1e40af",
    gradient: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
  },
  CN: {
    code: "CN",
    name: "China",
    icon: "fas fa-star",
    iconColor: "#dc2626",
    gradient: "linear-gradient(135deg, #fee2e2, #fecaca)",
  },
  IN: {
    code: "IN",
    name: "India",
    icon: "fas fa-om",
    iconColor: "#ea580c",
    gradient: "linear-gradient(135deg, #ffedd5, #fed7aa)",
  },
  DE: {
    code: "DE",
    name: "Germany",
    icon: "fas fa-industry",
    iconColor: "#dc2626",
    gradient: "linear-gradient(135deg, #fef3c7, #fde68a)",
  },
  FR: {
    code: "FR",
    name: "France",
    icon: "fas fa-monument",
    iconColor: "#1e40af",
    gradient: "linear-gradient(135deg, #dbeafe, #e0e7ff)",
  },
};

const DEFAULT_JURISDICTION: JurisdictionInfo = {
  code: "??",
  name: "Other",
  icon: "fas fa-globe",
  iconColor: "#64748b",
  gradient: "linear-gradient(135deg, #f1f5f9, #e2e8f0)",
};

export function getJurisdiction(code: string): JurisdictionInfo {
  return JURISDICTIONS[code?.toUpperCase()] || { ...DEFAULT_JURISDICTION, code: code || "??" };
}

/**
 * Renders a jurisdiction badge as an inline icon + text.
 * Use in JSX: <JurisdictionBadge code="US" />
 */
export function jurisdictionBadgeProps(code: string) {
  const j = getJurisdiction(code);
  return {
    icon: j.icon,
    iconColor: j.iconColor,
    gradient: j.gradient,
    name: j.name,
    code: j.code,
  };
}
